/**
 * `useMe` — TanStack Query hook for the current user.
 *
 * Validates the response through the shared UserSchema before handing
 * it to consumers. If validation fails, that's a kernel contract
 * violation; we throw (and TanStack Query surfaces the error to the
 * boundary).
 */

import { useQuery } from "@tanstack/react-query";
import { UserSchema, type User } from "@command-center/shared";
import { apiGet, UnauthenticatedError } from "./api";

export function useMe() {
  return useQuery<User, Error>({
    queryKey: ["me"],
    queryFn: async () => {
      const raw = await apiGet<unknown>("/api/me");
      const parsed = UserSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/me response failed UserSchema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    // 401 is not really an "error" from the UI's perspective — it's a
    // signal to render the signed-out view. We want it to flow through
    // as a query result: the consumer checks isError + UnauthenticatedError.
    throwOnError: (error) => !(error instanceof UnauthenticatedError),
    // Identity doesn't change while a tab is open. Overriding the
    // app-wide `refetchOnWindowFocus: true` here stops a spurious
    // /api/me round-trip every time an operator tabs away to read the
    // OTP email and back during sign-in — that refetch was churning the
    // Shell and resetting the in-progress sign-in form. Session expiry
    // mid-use is still caught: the cache subscriber in query-client.ts
    // invalidates ["me"] when any *other* query 401s, and `invalidate`
    // refetches regardless of this flag.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}
